import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Award, Calendar, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function ChallengeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ch, setCh] = useState(null);
  const [board, setBoard] = useState([]);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get(`/challenges/${id}`),
      api.get(`/challenges/${id}/leaderboard`),
    ]);
    setCh(a.data);
    setBoard(b.data);
  };

  useEffect(() => { load(); }, [id]);

  if (!ch) return <div className="max-w-7xl mx-auto p-10 text-zinc-500 font-mono">Loading…</div>;

  const myRow = user && board.find((r) => r.user_id === user.user_id);
  // IST-local YYYY-MM-DD (toISOString uses UTC; shifting by the user's tz offset
  // before slicing yields the day the runner is actually living in).
  const _now = new Date();
  const today = new Date(_now.getTime() - _now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
  const status = today < ch.start_date ? "UPCOMING" : today > ch.end_date ? "CLOSED" : "LIVE";

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="challenge-detail">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">CHALLENGE</p>
            <span className={`px-2 py-0.5 hairline font-mono text-[10px] tracking-widest uppercase ${
              status === "LIVE" ? "bg-orange-600/15 border-orange-600/40 text-orange-500"
              : status === "UPCOMING" ? "bg-zinc-900 border-zinc-700 text-zinc-300"
              : "bg-zinc-900 border-zinc-800 text-zinc-500"
            }`}>{status}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white mt-2">{ch.title}</h1>
          <p className="text-zinc-400 mt-3 max-w-2xl whitespace-pre-wrap">{ch.description}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <Mini label="START" value={ch.start_date} />
            <Mini label="END" value={ch.end_date} />
            <Mini label="TARGET DAYS" value={ch.target_days || "—"} />
            <Mini label="KM / DAY" value={ch.target_km_per_day ? `${ch.target_km_per_day}` : "—"} />
          </div>

          {ch.image && (
            <img src={ch.image} alt={ch.title} className="mt-6 w-full max-h-[420px] object-cover hairline" />
          )}

          {/* How to participate */}
          <div className="mt-8 hairline bg-zinc-900 p-5">
            <h3 className="font-display text-lg text-white tracking-wider mb-2">HOW IT WORKS</h3>
            <p className="text-zinc-300 text-sm leading-relaxed">
              Just <span className="text-orange-500 font-semibold">run, walk, or cycle</span> between
              <span className="text-white font-semibold"> {ch.start_date} → {ch.end_date}</span> and
              log it on the <Link to="/activity" className="text-orange-500 hover:underline">Activity</Link> tab
              — either manually or via your connected Strava. The leaderboard updates automatically based on
              your activities within the challenge window. No separate check-ins needed.
            </p>
            <Link to="/activity" className="mt-4 inline-flex">
              <Button data-testid="go-log-activity" className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider">
                LOG AN ACTIVITY <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>

          {myRow && (
            <div className="mt-6 hairline bg-orange-600/10 border-orange-600/40 p-4">
              <p className="font-mono text-[10px] text-orange-500 tracking-widest uppercase">YOUR STANDING</p>
              <p className="font-display text-2xl text-white mt-1">
                {myRow.total_km} KM · {myRow.days} ACTIVE DAYS
              </p>
              {ch.target_km_per_day > 0 && (
                <p className={`font-mono text-[10px] mt-1 tracking-widest uppercase ${myRow.on_target ? "text-orange-400" : "text-zinc-500"}`}>
                  {myRow.days_target_met || 0} / {myRow.days || 0} DAYS ON TARGET (≥ {ch.target_km_per_day} KM)
                  {myRow.on_target && " · STREAK INTACT"}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div>
          <h2 className="font-display text-2xl text-white mb-4 tracking-wider flex items-center gap-2">
            <Award className="text-orange-500" size={20} /> LEADERS
          </h2>
          {ch.target_km_per_day > 0 && (
            <p className="font-mono text-[10px] text-orange-500 tracking-widest uppercase mb-3">
              Consistency first · Runners who hit ≥ {ch.target_km_per_day} KM every day rank on top
            </p>
          )}
          <div className="hairline bg-zinc-900 divide-y divide-zinc-800">
            {board.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 font-mono text-sm">No activities logged yet</div>
            ) : board.map((r, i) => (
              <Link to={`/profile/${r.user_id}`} key={r.user_id}
                data-testid={`challenge-leaderboard-row-${i + 1}`}
                className={`p-4 flex items-center gap-3 transition hover:bg-zinc-900/60 ${user?.user_id === r.user_id ? "bg-orange-600/5" : ""}`}>
                <span className="font-display text-2xl text-orange-500 w-6">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm text-white truncate">{r.user_name}</p>
                    {r.on_target && (
                      <span
                        data-testid={`on-target-badge-${r.user_id}`}
                        className="font-mono text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 bg-orange-600 text-white"
                      >
                        ON TARGET
                      </span>
                    )}
                  </div>
                  <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
                    {r.days} DAYS · {r.total_km} KM
                    {ch.target_km_per_day > 0 && ` · ${r.days_target_met || 0} ON TARGET`}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          <p className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase mt-3">
            Powered by your logged activities · Auto-updated
          </p>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="hairline bg-zinc-900 p-4">
      <div className="font-display text-lg text-white">{value}</div>
      <div className="font-mono text-[10px] text-zinc-500 tracking-[0.25em] mt-1">{label}</div>
    </div>
  );
}
